// Novo arquivo: ModalCompanyAccess.tsx
import { Modal } from "flowbite-react";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { IEmployee } from "../../../types/employee.type";
import {
  loadCompanyAccesses,
  updateCompanyAccess,
} from "../../../common/services/companyaccess.api";
import Loading from "../../../common/components/ui/loading/Loading";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ICompanyAccess } from "@/types/companyaccess.type";
import { ApiResponseType } from "@/common/helpers/api.helpers";
import { useGetCompanies } from "@/common/hooks";
import FormCompanyAccess from "./FormCompanyAccess";

type Props = {
  employee: IEmployee;
  onClose: () => void;
};

type FormAccessData = {
  companyIds: string[];
  viewAll: boolean;
};
type QueryError = {
  status?: number;
  message?: string;
};

export default function ModalCompanyAccess(props: Props) {
  const { showToast } = useToastContext();
  const queryClient = useQueryClient();

  const notifySuccess = (text: string) => showToast({ text, type: "success" });
  const notifyError = (text: string) => showToast({ text, type: "error" });

  const handleCloseModal = () => {
    props.onClose();
  };

  const { data: allCompanies, isLoading: isLoadingCompanies } =
    useGetCompanies();

  const { data: currentAccess, isLoading: isLoadingAccess } = useQuery({
    queryKey: ["companyAccess", props.employee.email],
    queryFn: () => loadCompanyAccesses(props.employee.email),
    enabled: !!props.employee.email,
    retry: (failureCount, error: unknown) => {
      if (typeof error === "object" && error !== null && "status" in error) {
        const queryError = error as QueryError;
        if (queryError.status === 404) return false;
      }
      return failureCount < 3;
    },
  });

  const { mutateAsync: saveAccessMutation, isPending: isSaving } = useMutation({
    mutationFn: (dataToSave: FormAccessData) =>
      updateCompanyAccess(
        props.employee.email,
        dataToSave.companyIds,
        dataToSave.viewAll
      ),
  });

  async function saveAccess(dataToSave: FormAccessData) {
    try {
      const response = (await saveAccessMutation(
        dataToSave
      )) as ApiResponseType<ICompanyAccess>;

      const { ok, data, errors } = response;
      const finalErrors =
        errors && !Array.isArray(errors)
          ? [errors]
          : (errors as unknown[] | undefined);
      if (ok && data) {
        notifySuccess(
          `Acessos do colaborador "${props.employee.name}" atualizados!`
        );
        queryClient.invalidateQueries({
          queryKey: ["companyAccess", props.employee.email],
        });
        handleCloseModal();
        return { ok, data, errors: finalErrors };
      }

      if (!ok) {
        notifyError(response.message as string);
      }

      return { ok, data, errors };
    } catch (err: unknown) {
      notifyError("Erro ao tentar salvar acessos, tente novamente");

      let errorMessage = "Erro desconhecido ao salvar.";
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "object" && err !== null && "message" in err) {
        errorMessage = String((err as { message: unknown }).message);
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      return {
        ok: false,
        data: dataToSave,
        errors: [{ message: errorMessage, path: [] }],
      };
    }
  }

  const initialAccessData: FormAccessData = {
    companyIds:
      currentAccess && currentAccess.ok
        ? currentAccess.data.companies?.map((c) => c.companyId) || []
        : [],
    viewAll:
      currentAccess && currentAccess.ok
        ? currentAccess.data.viewAll || false
        : false,
  };

  const isLoading = isLoadingCompanies || isLoadingAccess;

  return (
    <Modal
      size={"4xl"}
      className="relative"
      show={true}
      onClose={() => handleCloseModal()}
    >
      <Modal.Header className="text-black">
        <div className="flex items-center justify-start gap-3 text-black">
          <span>Acessos de: {props.employee.name}</span>
        </div>
      </Modal.Header>
      <Modal.Body
        className="w-full relative border-gray flex flex-col text-black"
        style={{
          minHeight: "calc(50vh)",
          maxHeight: "calc(80vh)",
        }}
      >
        {isLoading ? (
          <Loading />
        ) : (
          <FormCompanyAccess
            data={initialAccessData}
            companyOptions={allCompanies || []}
            onSubmit={saveAccess}
            isSubmitting={isSaving}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}
